import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AppDispatch, RootState } from 'store'
import {
  getNotificationCenter,
  markAllNotificationCenterRead,
  markNotificationCenterRead,
} from 'features/notificationCenter/notificationCenterSlice'
import { INotificationItem } from 'types/notificationCenter.interface'
import { IdleBellIcon } from 'assets/icons/IdleBellIcon'
import { timeAgo } from 'utils/helpers'
import useAuth from 'hooks/useAuth'
import {
  BellWrap,
  BellButton,
  BellBadge,
  PanelContainer,
  PanelHead,
  PanelTitle,
  PanelAction,
  PanelList,
  PanelRow,
  RowDot,
  RowBody,
  RowTitle,
  RowMeta,
  PanelFooter,
  PanelEmpty,
} from './NotificationBell.styled'

export const NotificationBell = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { items, unreadCount } = useSelector((state: RootState) => state.notificationCenter)
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dispatch(getNotificationCenter({ roleId: user?.currentContract?.role?.id ?? null }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('click', onClick, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('click', onClick, true)
    }
  }, [open])

  const openItem = (n: INotificationItem) => {
    dispatch(markNotificationCenterRead(n.id))
    setOpen(false)
    if (n.link) navigate(n.link)
  }

  const recent = items.slice(0, 6)

  return (
    <BellWrap>
      <BellButton
        type='button'
        aria-label='Notifications'
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
      >
        {IdleBellIcon}
        {unreadCount > 0 && <BellBadge>{unreadCount > 99 ? '99+' : unreadCount}</BellBadge>}
      </BellButton>
      {open &&
        createPortal(
          <PanelContainer ref={panelRef}>
            <PanelHead>
              <PanelTitle>Alerts & Notifications</PanelTitle>
              {unreadCount > 0 && (
                <PanelAction type='button' onClick={() => dispatch(markAllNotificationCenterRead())}>
                  Mark all read
                </PanelAction>
              )}
            </PanelHead>
            <PanelList>
              {recent.length ? (
                recent.map((n) => (
                  <PanelRow key={n.id} type='button' $unread={!n.read} onClick={() => openItem(n)}>
                    <RowDot $sev={n.severity} />
                    <RowBody>
                      <RowTitle $unread={!n.read}>{n.title}</RowTitle>
                      <RowMeta>
                        {n.category} · {timeAgo(n.createdAt) || 'just now'}
                      </RowMeta>
                    </RowBody>
                  </PanelRow>
                ))
              ) : (
                <PanelEmpty>No notifications</PanelEmpty>
              )}
            </PanelList>
            <PanelFooter>
              <PanelAction
                type='button'
                onClick={() => {
                  setOpen(false)
                  navigate('/notifications')
                }}
              >
                View all ›
              </PanelAction>
            </PanelFooter>
          </PanelContainer>,
          document.body,
        )}
    </BellWrap>
  )
}
